import React from 'react';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { useLocation } from '@reach/router';
import { chakra, Box, Text, Container, Button, Link } from '@chakra-ui/react';
import Nav from './Nav';
import ComponentWrapper from './ComponentWrapper';
import { H1 } from '../styles/HeadingStyles';
import VideoPlayer from './VideoPlayer';
import NzGovtLogo from './NzGovtLogo';
import HomeAnimation from './HomeAnimation';
import CtaLink from './CtaLink';

const LoginContainer = chakra(Container, {
  baseStyle: {
    display: 'none',
    '@media only screen and (min-width: 1170px)': {
      textAlign: 'left',
      display: 'flex',
    },
  },
});

const ButtonBox = chakra(Box, {
  baseStyle: {
    button: '{home || nzHome || auHome "primary" : "primaryDark"}',
  },
});

const ButtonStyle = chakra(Button, {
  baseStyle: {
    a: {
      ':hover': {
        textDecoration: 'none',
      },
    },
  },
});

type Props = {
  title: string;
  subtitle?: {} | string;
  slug?: string;
  image?:
    | {
        video?: { thumbnailUrl: string; streamingUrl: string };
        gatsbyImageData?: IGatsbyImageData;
        alt?: string;
      }
    | boolean;
  includeCTA?: boolean;
  includeExternal?: boolean;
  targetAudience?: string;
  featuredMediaThumbTime?: string;
  headerClientLogos?: [];
  byline?: string | boolean;
  leftUtilitySection?: HTMLElement | string | {};
  textAlign?: string;
  locale?: string;
  externalLink?: string;
  externalButtonText?: string;
};
/** A simple header component which contains the navigation, a background image if required, section for header text */
const Header = ({
  title,
  subtitle,
  slug,
  image,
  includeCTA,
  includeExternal,
  targetAudience,
  featuredMediaThumbTime,
  headerClientLogos,
  byline,
  leftUtilitySection,
  textAlign,
  locale,
  externalLink,
  externalButtonText,
  ...props
}: Props) => {
  // Get the default image for the header.

  const location = useLocation();

  const home = location.pathname === '/';
  const nzHome = location.pathname === '/en-nz/';
  const auHome = location.pathname === '/en-au/';
  const ctHome = location.pathname === '/virus-features/';
  const ctHomeNz = location.pathname === '/en-nz/virus-features/';
  const safetySnap = location.pathname === '/safety-snap/';

  const ctPage = ctHome || ctHomeNz || auHome;

  const showBlue = home || nzHome || auHome || ctHome || ctHomeNz || safetySnap;

  return (
    <Container
      as="header"
      variant={showBlue ? 'darkSection' : 'whiteSection'}
      pt={[0, 8]}
      pb={['8', '16']}
      display="flex"
      flexWrap="wrap"
      justifyContent="center"
      textAlign={textAlign || 'left'}
      maxWidth="full"
    >
      <ComponentWrapper>
        <LoginContainer
          flexDirection="row-reverse"
          fontSize={0}
          lineHeight="1.167rem"
          py="0"
          maxWidth="maxWidth"
          mx="auto"
          flexBasis="100%"
          px={[5, 5, 5, 4]}
        >
          <a
            style={{ color: showBlue ? '#fff' : '#333' }}
            href="/sign-in"
          >
            Log In
          </a>
        </LoginContainer>

        <Nav home={showBlue} locale={locale} />
        <ComponentWrapper
          mt={[8, 32]}
          justifyContent="left"
          flexBasis="1110px"
          px={[0]}
          maxWidth="maxWidth"
        >
          <H1
            fontSize={[6, 64]}
            lineHeight={[5, 9]}
            color={showBlue ? 'white' : 'heading'}
            width="100%"
          >
            {title}
          </H1>
          <Box justifyContent="left" py="1" width="100%">
            {subtitle && (
              <Text
                fontSize={[3, 4]}
                lineHeight={[4, 5]}
                mb={[4, 5]}
                width={['100%', '60%']}
                fontWeight="light"
                color={showBlue ? 'white' : 'secondary'}
              >
                {subtitle}
              </Text>
            )}
            {leftUtilitySection && <div>{leftUtilitySection}</div>}
          </Box>

          <Box
            width={[3, '40%']}
            mb={[0, 0]}
            minHeight={image ? [0, '200px', '200px', '400px'] : ['0px', '0px']}
            display="flex"
            flexDirection="column"
          >
            {includeCTA && (
              <ButtonBox style={{ flexBasis: '100%' }}>
                <CtaLink
                  link={null}
                  variant={showBlue ? 'primaryDark' : 'primary'}
                  size="lg"
                />
              </ButtonBox>
            )}
            {includeExternal && (
              <ButtonBox style={{ flexBasis: '100%' }}>
                <ButtonStyle px="6" py="2" size="lg" variant="primary">
                  <Link sx={{ color: 'white' }} href={externalLink}>
                    {externalButtonText}
                  </Link>
                </ButtonStyle>
              </ButtonBox>
            )}
            {location.pathname === '/' ||
              (location.pathname === '/virus-features/' && (
                <Box
                  width={['0', '255px', '400px']}
                  mt={[0, 16, 0]}
                  alignSelf="baseline"
                >
                  <NzGovtLogo />
                </Box>
              ))}
          </Box>
          <Box
            width={['100%', '60%']}
            order={[-1, 0]}
            mb={[7, 0]}
            p={[4, 0]}
            alignSelf="flex-end"
          >
            {showBlue && <HomeAnimation />}
            {/* {nzHome && <HomeAnimation />} */}

            {!ctPage && image?.video && (
              <VideoPlayer
                thumbnailUrl={image.video.thumbnailUrl}
                featuredMediaThumbTime={featuredMediaThumbTime}
                streamingUrl={image.video.streamingUrl}
                bookDemo
              />
            )}

            {!ctPage && image && (
              <GatsbyImage
                image={image.gatsbyImageData}
                alt={image.alt || title}
                imgStyle={{ objectFit: 'contain' }}
                style={{ maxHeight: 400 }}
              />
            )}
          </Box>
        </ComponentWrapper>
      </ComponentWrapper>
    </Container>
  );
};

export default Header;
